import React, { useState } from 'react';
import { Modal, Form, Input, message, Button, Select, Typography, Space, Tag } from 'antd';
import { TGetTransactionResponse } from '@/types/transactions/transaction-types';
import { ProductOrder } from '@/types/cards/card-types';
import useCreateAPhysicalCard from '@/hooks/cards/useCreateAPhysicalCard';
import CardTaggingConfirmationModal from './CardTaggingConfirmationModal';

type TRegisterCardModalProps = {
  cardData: TGetTransactionResponse | ProductOrder;
  visible: boolean;
  onClose: () => void;
  zIndex?: number;
};

export enum PHYSICAL_CARDS_BRANDS {
  VERVE = 'verve',
  AFRIGO = 'afrigo',
}

const { Text } = Typography;

const CreateCardModal = (props: TRegisterCardModalProps) => {
  const { cardData, visible, onClose, zIndex } = props;
  const [form] = Form.useForm();
  const { data, isPending, mutate } = useCreateAPhysicalCard();
  const [showTaggingModal, setShowTaggingModal] = useState(false);
  const [cardholderName, setCardholderName] = useState<string | null>(null);

  const handleSubmit = async (values: { pan: string; card_brand: string }) => {
    mutate(
      {
        card_id: cardData.id,
        pan: values.pan,
        card_brand: values.card_brand,
      },
      {
        onSuccess: (response) => {
          message.success('Card registered successfully');
          setCardholderName(response.card_holdername || null);
          setShowTaggingModal(true);
          form.resetFields();
          // Close the registration modal when tagging modal appears
          onClose();
        },
      }
    );
  };

  // Type guard for ProductOrder (batch)
  const isBatch = (data: any): data is ProductOrder => {
    return data && typeof data === 'object' && 'account_name' in data;
  };

  return (
    <>
      <Modal
        title={isBatch(cardData) ? 'Register Batch Cards' : 'Register Physical Card'}
        open={visible}
        onCancel={onClose}
        footer={null}
        zIndex={zIndex}
      >
        <Form
          form={form}
          layout='vertical'
          style={{ marginTop: '20px' }}
          onFinish={handleSubmit}
        >
          <Form.Item
            name='pan'
            label='Card PAN'
            rules={isBatch(cardData) ? [] : [
              { required: true, message: 'Please enter the card PAN' },
              { pattern: /^\d{16,19}$/, message: 'PAN must be 16 to 19 digits' },
            ]}
          >
            <Input placeholder='Enter 16-digit card number' maxLength={19} disabled={isBatch(cardData)} />
          </Form.Item>

          <Form.Item
            name='card_brand'
            label='Card brand'
            rules={isBatch(cardData) ? [] : [{ required: true, message: 'Please select a card brand!' }]}
          >
            <Select disabled={isBatch(cardData)}>
              <Select.Option value={PHYSICAL_CARDS_BRANDS.VERVE}>
                VERVE
              </Select.Option>
              <Select.Option value={PHYSICAL_CARDS_BRANDS.AFRIGO}>
                AFRIGO
              </Select.Option>
            </Select>
          </Form.Item>

          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
            {isBatch(cardData) ? (
              <>
                <Text strong>Batch Details</Text>
                <div style={{ marginTop: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div><Text type="secondary">Reference:</Text> <Text code>{cardData.id}</Text></div>
                    <div><Text type="secondary">Batch Name:</Text> {cardData.account_name}</div>
                    <div>
                      <Text type="secondary">Number of Cards:</Text>{' '}
                      <Tag color="blue">{cardData.cards_count || cardData.count || '-'} cards</Tag>
                    </div>
                    <div>
                      <Text type="secondary">Status:</Text>{' '}
                      <Tag color="orange">{cardData.status.toUpperCase()}</Tag>
                    </div>
                    <div>
                      <Text type="secondary">Created:</Text>{' '}
                      {cardData.created ? new Date(cardData.created).toLocaleString() : '-'}
                    </div>
                  </Space>
                </div>
              </>
            ) : (
              <>
                <Text strong>Transaction Details</Text>
                <div style={{ marginTop: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div><Text type="secondary">Reference:</Text> <Text code>{cardData?.reference}</Text></div>
                    <div>
                      <Text type="secondary">Amount:</Text>{' '}
                      <Tag color="green">
                        {cardData?.dest_currency_code} {cardData?.dest_amount}
                      </Tag>
                    </div>
                    <div>
                      <Text type="secondary">Card Holder:</Text>{' '}
                      <Text strong>{cardData?.disburse_recipient_name}</Text>
                    </div>
                  </Space>
                </div>
              </>
            )}
          </div>

          {!isBatch(cardData) && (
            <Form.Item>
              <Button type='primary' htmlType='submit' loading={isPending}>
                Register Card
              </Button>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <CardTaggingConfirmationModal
        open={showTaggingModal}
        cardholderNames={cardholderName ? [cardholderName] : []}
        onConfirm={() => {
          setShowTaggingModal(false);
          setCardholderName(null);
          onClose();
        }}
      />
    </>
  );
};

export default CreateCardModal;
